import { Button } from 'components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/shadcn/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/shadcn/table';
import { Pencil, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role, User } from 'types';
import { apiUrl, fetchWithAuth } from 'utils';
import { Checkbox } from 'components/shadcn/checkbox';
import { Skeleton } from 'components/shadcn/skeleton';

export const UserList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [chats, setChats] = useState<User['chats']>([]);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [dialogEmail, setDialogEmail] = useState('');
  const [dialogPassword, setDialogPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState(genPass());
  const [dialogRole, setDialogRole] = useState<Role>();
  const [dialogChats, setDialogChats] = useState<string[]>([]);
  const [dialogCanEditDataToExtract, setDialogCanEditDataToExtract] = useState(false);
  const [dialogCanEditDynamicPrompts, setDialogCanEditDynamicPrompts] = useState(false);
  const [dialogCanManageRag, setDialogCanManageRag] = useState(false);

  useEffect(() => {
    setIsFetchingUsers(true);
    Promise.all([
      fetchUsers().then((data) => setUsers(data)),
      fetchChats().then((data) => setChats(data)),
    ]).finally(() => {
      setIsFetchingUsers(false);
    });
  }, []);

  useEffect(() => {
    setDialogEmail(selectedUser?.email || '');
    setDialogRole(selectedUser?.role || undefined);
    setDialogChats(selectedUser?.chats?.map((chat) => chat.id) || []);
    setDialogPassword('');
    setDialogCanEditDataToExtract(selectedUser?.canEditDataToExtract || false);
    setDialogCanEditDynamicPrompts(selectedUser?.canEditDynamicPrompts || false);
    setDialogCanManageRag(selectedUser?.canManageRag || false);
  }, [selectedUser]);
  const isEditing = !!selectedUser && !isCreating;

  async function fetchChats() {
    try {
      const response = await fetchWithAuth(`${apiUrl}/chat-metadata`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching chats:', error);
      return [];
    }
  }

  async function fetchUsers() {
    try {
      const response = await fetchWithAuth(`${apiUrl}/users`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  async function submitDialog() {
    const url = isCreating ? `${apiUrl}/users` : `${apiUrl}/users/${selectedUser?.id}`;
    const method = isCreating ? 'POST' : 'PUT';
    const body = JSON.stringify({
      email: dialogEmail,
      password: dialogPassword || undefined,
      role: dialogRole,
      chats: dialogChats,
      canEditDataToExtract: dialogCanEditDataToExtract,
      canEditDynamicPrompts: dialogCanEditDynamicPrompts,
      canManageRag: dialogCanManageRag,
    });

    try {
      setIsLoading(true);
      await fetchWithAuth(url, { method, body });
      setUsers(await fetchUsers());
      setIsCreating(false);
      setSelectedUser(undefined);
    } catch (error) {
      console.error('Error submitting dialog:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="mx-auto py-6 px-4 lg:px-6 lg:max-w-screen-lg flex-grow self-start box-border w-full animate-in fade-in duration-500">
      <div className="flex flex-row gap-2 justify-between items-center mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-0">
        <h1 className="text-4xl font-black text-primary text-balance">
          {t('dashboard.users.title')}
        </h1>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="me-2 h-4 w-4" /> {t('dashboard.users.createUser')}
        </Button>
      </div>
      <div className="rounded-md border animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-100">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('dashboard.users.table.email')}</TableHead>
              <TableHead>{t('dashboard.users.table.role')}</TableHead>
              <TableHead>{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetchingUsers
              ? Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-[250px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))
              : users?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>
                      <Button variant="link" size="icon" onClick={() => setSelectedUser(user)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>
      <Dialog
        open={isCreating || isEditing}
        onOpenChange={() => {
          setSelectedUser(undefined);
          setIsCreating(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isCreating ? t('dashboard.users.createUser') : t('dashboard.users.editUser')}
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex flex-col gap-4 max-h-[500px] overflow-y-auto p-4"
            style={{ scrollbarColor: '#E0E0E3 transparent;' }}
          >
            <div>
              <Label htmlFor="email">{t('dashboard.users.fields.email')}</Label>
              <Input
                id="email"
                type="email"
                value={dialogEmail}
                required
                onChange={(e) => setDialogEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password">{t('dashboard.users.fields.password')}</Label>
              <Input
                id="password"
                type="password"
                name="password"
                autoComplete="new-password"
                required={isCreating}
                value={dialogPassword}
                onChange={(e) => setDialogPassword(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Input value={generatedPassword} onFocus={(e) => e.target.select()} readOnly />
              <Button
                type="button"
                variant="outline"
                onClick={() => setGeneratedPassword(genPass())}
              >
                Generate
              </Button>
            </div>
            <div>
              <Label htmlFor="role">{t('dashboard.users.fields.role')}</Label>
              <Select
                onValueChange={(value) => setDialogRole(value as Role)}
                value={dialogRole}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('dashboard.users.fields.selectRole')}>
                    {dialogRole || t('dashboard.users.fields.selectRole')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent position="popper">
                  {Object.values(Role).map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('dashboard.users.permissions.title')}</Label>
              <div className="flex flex-row gap-2 items-center">
                <Checkbox
                  checked={dialogCanEditDataToExtract}
                  onCheckedChange={(checked) => setDialogCanEditDataToExtract(!!checked)}
                  id="canEditDataToExtract"
                />
                <label htmlFor="canEditDataToExtract" className="cursor-pointer">
                  {t('dashboard.users.permissions.canEditDataToExtract')}
                </label>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <Checkbox
                  checked={dialogCanEditDynamicPrompts}
                  onCheckedChange={(checked) => setDialogCanEditDynamicPrompts(!!checked)}
                  id="canEditDynamicPrompts"
                />
                <label htmlFor="canEditDynamicPrompts" className="cursor-pointer">
                  {t('dashboard.users.permissions.canEditDynamicPrompts')}
                </label>
              </div>

              <div className="flex flex-row gap-2 items-center">
                <Checkbox
                  checked={dialogCanManageRag}
                  onCheckedChange={(checked) => setDialogCanManageRag(!!checked)}
                  id="canManageRag"
                />
                <label htmlFor="canManageRag" className="cursor-pointer">
                  {t('dashboard.users.permissions.canManageRag')}
                </label>
              </div>
            </div>
            <div>
              <Label>{t('dashboard.users.chats')}</Label>
              {chats?.map((chat) => (
                <div className="flex flex-row gap-2 items-center" key={chat.id}>
                  <Checkbox
                    id={chat.id}
                    checked={dialogChats.includes(chat.id)}
                    onCheckedChange={(checked) =>
                      setDialogChats((prev) =>
                        checked ? [...prev, chat.id] : prev.filter((id) => id !== chat.id),
                      )
                    }
                  />
                  <label htmlFor={chat.id} className="cursor-pointer">
                    {chat.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreating(false)} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitDialog} disabled={isLoading}>
              {isCreating ? t('common.create') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function genPass(length = 20) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}
